import { tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import type { CardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { handleError } from "cc-frontend/error-handling"
import copyLessonAction from "cc-frontend/lib/actions/lesson/LESSON_COPY"
import lessonViewHistoryActioon from "cc-frontend/lib/actions/lesson/LESSON_VIEW_HISTORY"
import { devLessonEditorSwitch } from "cc-frontend/lib/dev-lesson-editor-switch"
import type CourseCalendarEditor from "cc-frontend/services/course-calendar-editor"
import type Dialog from "cc-frontend/services/dialog"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type ModalManager from "cc-frontend/services/modal-manager"
import type PatchCoordinator from "cc-frontend/services/patch-coordinator"
import type PlanbookEditor from "cc-frontend/services/planbook-editor"
import type Session from "cc-frontend/services/session"
import _, { isNil } from "lodash"
import moment from "moment"
import styles from "./body.module.css"

@tagName("")
export default class LessonMenuPrimary extends Component {
  readonly styles = styles

  // args
  modelsNeededForFirstEdit!: ModelsNeededForFirstEdit
  editCourseTitleAndOrder!: Function
  closeMenu!: Function
  courseDate!: CourseDateCombined
  planbook!: Planbook
  rotationCalendar!: RotationCalendar
  course!: Course
  lesson!: CardStack
  dateString!: string // TODO should probably change so it's just use courseDate.dateString and remove this arg

  // props
  isShowingPrimaryMenu = false
  secondaryMenu: null | string = null

  // service
  @service declare dialog: Dialog
  @service("ember-store") store
  @service dispatcher
  @service notifier
  @service rpc
  @service lessonEditor!: LessonEditor
  @service session!: Session
  @service planbookEditor!: PlanbookEditor
  @service courseCalendarEditor!: CourseCalendarEditor
  @service modalManager!: ModalManager
  @service("patch-coordinator") patchCoordinator!: PatchCoordinator

  @computed("args.models.courseDate.timeDefault")
  get hasTime() {
    return this.courseDate.timeDefault !== null
  }

  @action
  editCourse() {
    this.editCourseTitleAndOrder(this.course.id)
    this.closeMenu()
  }

  get isDevMode() {
    return devLessonEditorSwitch.isEnabled
  }

  @action
  switchToUnifiedEditor() {
    this.closeMenu()
    devLessonEditorSwitch.toggle(
      this.patchCoordinator,
      this.lesson,
      this.course.id,
      this.dateString,
    )
  }

  @action
  showChangeClassOrderMenu() {
    this.set("isShowingPrimaryMenu", false)
    this.set("secondaryMenu", "CHANGE_CLASS_ORDER")
  }

  @action
  copyLesson() {
    this.closeMenu()

    if (this.session.isAuthenticated !== true) {
      this.session.mustSignInToProceed("Copy Lesson")
      return
    }

    if (this.session.userId !== null) {
      this.dispatcher.dispatchPreparedAction(
        copyLessonAction({
          lessonToCopy: this.lesson,
          cardStackId: this.lesson.id,
          currentUserId: this.session.userId,
          ownerIds: [this.planbook.relationships.owner.data.id],
          planbookId: this.planbook.id,
          courseId: this.course.id,
        }),
      )
    }

    this.notifier.success("", {
      title: `Lesson Copied!`,
      message: "Click a lesson's paste button to paste.",
      duration: 1000,
    })
  }

  @action
  pasteLesson() {
    if (!isNil(this.session.userId)) {
      this.store.find("user", this.session.userId).then((user) => {
        if (isNil(user?.attributes?.clipboard?.cardStackAttributes)) return

        let newCardStackAttributes = _.cloneDeep(user.attributes.clipboard.cardStackAttributes)

        this.lessonEditor.pasteCardStack(
          this.modelsNeededForFirstEdit,
          this.lesson.id,
          this.course,
          this.planbook,
          newCardStackAttributes,
          this.lesson.attributes,
          "lesson",
          newCardStackAttributes.parentCardStack,
        )
      })
    }
    this.closeMenu()
  }

  @action
  eraseLesson() {
    this.dialog
      .blank("dialogs/confirm-delete-lesson", {
        shouldRemoveHistory: true,
        className: "dialog--create-annotation",
        isNonInstructional: this.course.attributes.isNonInstructional,
      })
      .then((shouldRemoveHistory: boolean) => {
        if (this.courseDate.cardStackId) {
          if (shouldRemoveHistory) {
            this.lessonEditor.eraseLesson(
              this.course,
              this.courseDate.dateString,
              this.courseDate.cardStackId,
              this.courseDate.timeCustom,
            )
          } else {
            this.lessonEditor.clearLesson(this.lesson)
          }
        }

        // TODO  CLOSE_LESSON_SIDEBAR
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
    this.closeMenu()
  }

  @action
  printOrDownload() {
    //pass in medium as param
    this.modalManager.openModal("planner/modals/print-download", {
      courseId: this.course.id,
      planbookId: this.planbook.id,
      startDate: this.dateString,
      endDate: this.dateString,
      layout: "course-date:single",
      currentUserId: this.session.userId,
      canPrint: true,
      dateRangeString: moment(this.dateString).format("dddd, MMMM Do YYYY"),
    })
    this.closeMenu()
  }

  @action
  fullScreen() {
    this.modalManager.openModal("planner/modals/full-screen", {
      course: this.course,
      planbook: this.planbook,
      rotationCalendar: this.rotationCalendar,
      dateString: this.courseDate.dateString,
      editCourseTitleAndOrder: this.editCourseTitleAndOrder,
      classNames: "take-course-background-color",
    })
    this.closeMenu()
  }

  @action
  importLessonsOrUnits() {
    this.modalManager.openModal("planner/modals/import-lessons", {
      dateString: this.courseDate.dateString,
      course: this.course,
    })
    this.closeMenu()
  }

  @action
  shareToGoogleClassroom() {
    // Find the public cards
    let publicCards = _.chain(this.lesson.attributes.cards)
      .reject((card) => card.type === "card-course-slot")
      // @ts-ignore we filter for the right type above
      .filter((card) => card.attributes.isPublic === true)
      .value()
    let publicCardsCount = publicCards.length
    let publicCardsCountString = publicCardsCount === 1 ? "1 card" : `${publicCardsCount} cards`

    // Format the URL to share
    let title = encodeURI(this.lesson.attributes.title || "")
    let userId = this.session.userId
    let url = encodeURI(
      `${window.location.protocol}//${window.location.host}/student-lesson/${this.course.id}/${this.dateString}`,
    )
    // let body = _.chain(publicCards)
    //   .map(card => {
    //     return [`${card.attributes.title}`, `${card.attributes.value}`]
    //   })
    //   .flatten()
    //   .join("\n")
    //   .value()

    let sharingUrl = `https://classroom.google.com/share?url=${url}&title=${title}`

    // Show some notices
    if (publicCardsCount === 0) {
      window
        .swal({
          title: `Before you share, at least one card needs to be visible on your<br>class website`,
          text: `Hover over a card, click the globe icon, and choose<br>"Show on class website"`,
          type: "error",
        })
        .catch((error) => {
          if (error instanceof Error) handleError(error)
        })
    } else {
      window
        .swal({
          title: `<b>Important Note</b>
          Only the ${publicCardsCountString} you've set to be "Visible on the Class Website" will be posted to Google Classroom.`,
          // text: "To make a card visible, click the globe icon and choose 'Show on class website'.",
          type: "info",
        })
        .then((_val) => {
          window.open(sharingUrl)
          this.dispatcher.dispatch("USER_SET_FLAG", {
            userId: userId,
            field: "googleClassroomExplainerWasShown",
            value: true,
          })
        })
        .catch((error) => {
          if (error instanceof Error) handleError(error)
        })
    }
    this.closeMenu()
  }

  @action
  saveAsTemplate() {
    window
      .swal({
        text: "What would you like to call this template?",
        type: "question",
        input: "text",
        inputPlaceholder: "Template name",
        showCancelButton: true,
      })
      .then((val) => {
        this.planbookEditor.saveAsLessonTemplate(
          this.course,
          this.planbook,
          this.lesson.attributes,
          val,
        )
        this.closeMenu()
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }

  @action
  bumpLessonForward() {
    this.closeMenu()
    if (this.courseDate) {
      this.courseCalendarEditor.bumpLesson(
        this.course,
        this.courseDate.dateString,
        this.planbook,
        this.rotationCalendar,
      )
    }
  }

  @action
  deleteAndShiftBackwards() {
    let doesNotPassUnitCheck = this.courseCalendarEditor.doesCourseDateHaveTinyUnit(
      this.course,
      this.courseDate.dateString,
    )
    this.closeMenu()

    if (doesNotPassUnitCheck) {
      window
        .swal({
          title:
            "To pull lessons backwards, you need to first delete the unit that starts and ends on this date",
          text: "Head over to the unit planner to delete the unit or make it longer.",
          type: "warning",
          showCancelButton: false,
        })
        .catch((error) => {
          if (error instanceof Error) handleError(error)
        })
    } else {
      let courseDateHasLesson = this.courseCalendarEditor.doesCourseDateHaveLesson(
        this.course,
        this.courseDate.dateString,
      )

      if (courseDateHasLesson) {
        window
          .swal({
            title: "To pull future lessons back to this day, we need to delete this lesson.",
            text: "Trying to pull this lesson backwards? Go to the lesson before this lesson and then pull lessons backwards.",
            type: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete this lesson",
            cancelButtonText: "Cancel",
          })
          .then(() => {
            this.courseCalendarEditor.pullLessonBackwards(
              this.courseDate.dateString,
              this.course,
              this.planbook,
              this.rotationCalendar,
            )
          })
          .catch((error) => {
            if (error instanceof Error) handleError(error)
          })
      } else {
        this.courseCalendarEditor.pullLessonBackwards(
          this.courseDate.dateString,
          this.course,
          this.planbook,
          this.rotationCalendar,
        )
      }
    }
  }

  @action
  viewVersions() {
    this.modalManager.openModal("planner/modals/lesson-history", {
      cardStack: this.lesson,
    })
    this.dispatcher.dispatchPreparedAction(
      lessonViewHistoryActioon({
        cardStackId: this.lesson.id,
        planbookId: this.planbook.id,
        courseId: this.course.id,
      }),
    )
    this.closeMenu()
  }
}
